import { useTranslation } from "react-i18next";
import {
  Avatar,
  Box,
  Chip,
  TableCell,
  TableRow,
  Typography,
} from "@material-ui/core";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import LockOpenOutlinedIcon from "@material-ui/icons/LockOpenOutlined";
import styled from "styled-components";
import dayjs from "dayjs";
import EditBtn from "./EditBtn";
import {
  UpdateUserBody,
  UserResource,
  UserResourceList,
} from "@dashboard-v3/api";
import EmptyItem from "./EmptyItem";
import DeleteBtn from "./DeleteBtn";
import TruncatedText from "components/TruncatedText";
import { UserSaveProps } from "components/UserFormModal";
import { getUser } from "utils/authentication";
import UserSkeletonRow from "../UserSkeletonRow";

type Params = {
  users: UserResourceList;
  pendingIds: Set<string>;
  onDelete: (userId: string) => void;
  onChange: (id: string, changes: UpdateUserBody) => Promise<void>;
};

export default function Items({
  users,
  pendingIds,
  onDelete,
  onChange,
}: Params) {
  const { t } = useTranslation("users");

  const onSaveUser = (user: UserSaveProps) => {
    return onChange(user.id, {
      name: user.name,
      password: user.password,
      role: user.role,
    });
  };

  if (users.length === 0) {
    return <EmptyItem>{t("items.noUsersForOrganization")}</EmptyItem>;
  }

  return (
    <>
      {users.map(user => {
        const { name, email, id, role, isPasswordSet, updatedAt } = user;

        if (pendingIds.has(id)) {
          return (
            <UserSkeletonRow key={email} testId="user-list__item-skeleton" />
          );
        }

        const disabled = isActionDisabled(user);
        const roleChipVariant = role === "OWNER" ? "default" : "outlined";
        const accessLabel = isPasswordSet ? t("passwordSet") : t("ssoOnly");
        const AccessIcon = isPasswordSet
          ? LockOutlinedIcon
          : LockOpenOutlinedIcon;

        return (
          <TableRow
            data-testid="user-list__item"
            key={email}
            style={{ height: 60 }}
          >
            <TableCell>
              <NameCell>
                <StyledAvatar>{getInitials(name, email)}</StyledAvatar>
                {name ? (
                  <Typography variant="body2">{name}</Typography>
                ) : (
                  <Typography
                    variant="body2"
                    style={{ color: "rgb(170, 170, 170)", fontStyle: "italic" }}
                  >
                    {t("items.noName")}
                  </Typography>
                )}
              </NameCell>
            </TableCell>
            <TableCell style={{ maxWidth: 300 }}>
              <TruncatedText
                text={email}
                variant="body2"
                color="textSecondary"
              />
            </TableCell>
            <TableCell>
              <Chip
                label={t(`userRole.${role}`)}
                variant={roleChipVariant}
                size="small"
                color="primary"
                style={{ paddingTop: 2 }}
              />
            </TableCell>
            <TableCell>
              <AccessLabel hasPassword={Boolean(isPasswordSet)}>
                <AccessIcon fontSize="small" />
                {accessLabel}
              </AccessLabel>
            </TableCell>
            <TableCell>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ whiteSpace: "nowrap" }}
              >
                {dayjs(updatedAt).fromNow()}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Box style={{ justifySelf: "end" }}>
                <EditBtn user={user} onSave={onSaveUser} disabled={disabled} />
                {users.length > 1 && (
                  <DeleteBtn
                    userId={id}
                    onDelete={onDelete}
                    disabled={disabled}
                  />
                )}
              </Box>
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
}

function isActionDisabled(userToEdit: UserResource) {
  const currentUser = getUser();

  if (currentUser.role === "ADMIN" && userToEdit.role === "OWNER") {
    return true;
  }

  return false;
}

function getInitials(name: string, email: string) {
  if (name) {
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map(part => part.charAt(0))
      .join("")
      .toUpperCase();
  }

  return (email.charAt(0) || "?").toUpperCase();
}

const NameCell = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`;

const StyledAvatar = styled(Avatar)`
  background-color: #e3f4fb;
  color: #04688a;
  font-size: 0.85rem;
  height: 34px;
  text-transform: uppercase;
  width: 34px;
`;

const AccessLabel = styled.span<{ hasPassword: boolean }>`
  color: ${props => (props.hasPassword ? "#2e7d52" : "rgba(0, 0, 0, 0.54)")};
  display: inline-flex;
  font-size: 0.875rem;
  gap: 6px;
  white-space: nowrap;

  svg {
    font-size: 1.05rem;
  }
`;
